import { ChevronDown } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'

/** A titled card. Every grouped block (rail sections, Today cards, billing packet) is one of these. */
export function Section({
  title,
  icon,
  count,
  actions,
  children,
  collapsible = false,
  defaultOpen = true,
  className,
  bodyClassName,
}: {
  title: React.ReactNode
  icon?: React.ReactNode
  count?: number
  actions?: React.ReactNode
  children?: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  className?: string
  bodyClassName?: string
}) {
  const [open, setOpen] = React.useState(defaultOpen)
  const id = React.useId()
  const header = (
    <>
      {icon ? <span className="text-muted-foreground [&_svg]:size-4">{icon}</span> : null}
      <span className="flex-1 text-left font-semibold text-foreground">{title}</span>
      {count !== undefined ? <span className="tabular text-lg font-semibold">{count}</span> : null}
    </>
  )
  return (
    <section className={cn('rounded-lg border border-border bg-card shadow-xs', className)}>
      <div className="flex items-center gap-2 px-3.5 py-2.5">
        {collapsible ? (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen((o) => !o)}
            className="flex flex-1 cursor-pointer items-center gap-2"
          >
            {header}
            <ChevronDown className={cn('size-4 text-subtle transition-transform', !open && '-rotate-90')} />
          </button>
        ) : (
          header
        )}
        {actions}
      </div>
      {open ? (
        <div id={id} className={cn('border-t border-border px-3.5 py-3', bodyClassName)}>
          {children}
        </div>
      ) : null}
    </section>
  )
}

/** Label / value row with optional provenance (hover shows where the fact came from). */
export function FactRow({
  icon,
  label,
  value,
  source,
}: {
  icon?: React.ReactNode
  label: string
  value: React.ReactNode
  source?: string
}) {
  return (
    <div className="flex items-start gap-2.5 py-1.5 text-sm" title={source ? `Source: ${source}` : undefined}>
      {icon ? <span className="mt-0.5 text-muted-foreground [&_svg]:size-4">{icon}</span> : null}
      <span className="w-24 shrink-0 font-medium text-foreground">{label}</span>
      <span className="min-w-0 flex-1 text-muted-foreground">{value}</span>
    </div>
  )
}
